// NotificationItem.js - Individual notification display component

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNotifications } from './NotificationContext';
import { useChat } from '../Chat/ChatContext';
import { supabase } from '../data/supabaseClient';
import { CustomButton10, CustomButton10Ca, CustomButton10Co } from '../Templates';
import { 
  getTypeIcon, 
  formatRelativeTime, 
  generateActionUrl, 
  getCategoryColor,
  getPriorityClass,
  requiresAction,
  getActionLabels,
  truncateMessage
} from './NotificationUtils';
import './Notifications.css';

/**
 * NotificationItem component - displays individual notification with actions
 */
const NotificationItem = ({ 
  notification, 
  showActions = true, 
  compact = false,
  onActionComplete = null 
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();
  const { openChat } = useChat();
  
  const {
    id,
    type,
    category,
    message,
    message_template,  // NEW: template for translation
    message_params,    // NEW: params for translation
    data,
    related_id,
    action_url,
    priority,
    is_read,
    read_at,
    created_at,
    actor_id,
    actor_name,
    actor_avatar_url
  } = notification;

  // Get icon and styling
  const IconComponent = getTypeIcon(type);
  const categoryColor = getCategoryColor(category);
  const priorityClass = getPriorityClass(priority);
  const actionLabels = getActionLabels(type);
  const needsAction = requiresAction(type);

  // IMPORTANT: Translate the notification message to user's language
  const displayMessage = React.useMemo(() => {
    
    // If we have template and params, translate it
    if (message_template && message_params) {
    try {
    // Ensure template key is in correct format
    let translationKey = message_template;
    if (translationKey.startsWith('notification.')) {
    translationKey = translationKey.replace('notification.', 'notifications.');
    }
    
    // Parse params if it's a string
    let params = message_params;
    if (typeof params === 'string') {
    try {
    params = JSON.parse(params);
    } catch (e) {
    console.warn('Failed to parse message_params:', params);
    params = {};
    }
    }
    
    // Ensure we have the sender_name in params, fallback to actor_name or 'Someone'
    const translationParams = {
    ...params,
    sender_name: params.sender_name || actor_name || params.sender || 'Someone'
    };
    
    
    // Translate in user's current language with better options
    const translated = t(translationKey, {
    ...translationParams,
    lng: t.language, // Force current user language
    interpolation: { 
        escapeValue: false,
        prefix: '{{',
        suffix: '}}'
      }
    });
    
    // Check if translation worked (didn't just return the key)
    if (translated && translated !== translationKey && !translated.includes('{{')) {
    return translated;
    } else {
    console.warn('⚠️ NotificationItem translation failed, using manual replacement');
    
    // Try alternative translation approach - direct key mapping
    let fallbackTranslated;
    if (translationKey === 'notifications.conversation_started_by') {
      fallbackTranslated = t('notifications.new_conversation', translationParams);
        if (fallbackTranslated && !fallbackTranslated.includes('{{')) {
            return fallbackTranslated;
        }
      }
      
      // Manual fallback - replace common placeholders in original message
    let fallbackMessage = message || `${translationParams.sender_name} started a conversation`;
      if (fallbackMessage && fallbackMessage.includes('{{sender_name}}')) {
        fallbackMessage = fallbackMessage.replace(/\{\{sender_name\}\}/g, translationParams.sender_name);
        }
          if (fallbackMessage && fallbackMessage.includes('{{actor_name}}')) {
            fallbackMessage = fallbackMessage.replace(/\{\{actor_name\}\}/g, translationParams.sender_name);
          }
          return fallbackMessage;
      }
    } catch (error) {
    console.error('❌ NotificationItem translation error:', error);
      // Last resort: try manual replacement
      let fallbackMessage = message || `${actor_name || 'Someone'} sent you a notification`;
    if (fallbackMessage && fallbackMessage.includes('{{sender_name}}')) {
        fallbackMessage = fallbackMessage.replace(/\{\{sender_name\}\}/g, actor_name || 'Someone');
      }
    return fallbackMessage;
    }
    }
    
    // No template - check if message has placeholders and try to replace them
    if (message && message.includes('{{')) {
      let processedMessage = message;
      if (message.includes('{{sender_name}}')) {
          processedMessage = processedMessage.replace(/\{\{sender_name\}\}/g, actor_name || 'Someone');
        }
        if (message.includes('{{actor_name}}')) {
          processedMessage = processedMessage.replace(/\{\{actor_name\}\}/g, actor_name || 'Someone');
        }
        return processedMessage;
      }
      
      // Return original message or create a default one
      return message || `${actor_name || 'Someone'} sent you a notification`;
  }, [message, message_template, message_params, actor_name, t, id]);

  // Handle notification click
  const handleNotificationClick = async () => {
    // Mark as read if not already read
    if (!is_read) {
      await markAsRead(id);
    }

    // Handle different notification types
    switch (type) {
      case 'new_message':
      case 'new_conversation':
      // Ride/package flow notifications: related_id is the conversation id, so
      // clicking opens the chat where the action happened (not the post).
      case 'ride_request':
      case 'ride_accepted':
      case 'ride_declined':
      case 'ride_time_proposed':
      case 'ride_time_agreed':
      case 'ride_arrived':
      case 'ride_no_show_reported':
      case 'ride_cancelled':
      case 'ride_completed':
      case 'package_request':
      case 'package_accepted':
      case 'package_declined':
      case 'package_time_proposed':
      case 'package_time_agreed':
      case 'package_arrived':
      case 'package_no_show_reported':
      case 'package_cancelled':
      case 'package_completed':
      case 'package_pickup':
      case 'package_delivered':
        if (related_id || actor_id) {
          openChat(related_id, actor_id);
        }
        break;

      case 'new_review':
      case 'new_ride_review':
      case 'new_package_review':
        // Navigate to user profile to see reviews
        navigate('/MyprofileForm');
        break;
        
      case 'event_invitation':
      case 'event_reminder':
      case 'event_cancelled':
        // Navigate to events page or specific event
        if (related_id) {
          navigate(`/events`);
        }
        break;
        
      case 'group_invitation':
      case 'new_group_ride':
      case 'group_discussion':
        // Navigate to groups page
        if (related_id) {
          navigate('/groups');
        }
        break;
        
      default:
        break;
    }

    // Call completion callback if provided
    if (onActionComplete) {
      onActionComplete('view', notification);
    }
  };

  // Handle accept action
  const handleAccept = async (e) => {
    e.stopPropagation();
    
    // Mark as read
    if (!is_read) {
      await markAsRead(id);
    }

    // Handle accept logic based on type
    switch (type) {
      case 'event_invitation':
        // TODO: Implement event invitation acceptance
        break;
      case 'group_invitation':
        // TODO: Implement group invitation acceptance
        break;
      default:
        break;
    }

    if (onActionComplete) {
      onActionComplete('accept', notification);
    }
  };

  // Handle decline action
  const handleDecline = async (e) => {
    e.stopPropagation();
    
    // Mark as read
    if (!is_read) {
      await markAsRead(id);
    }

    // Handle decline logic based on type
    switch (type) {
      case 'event_invitation':
        // TODO: Implement event invitation decline
        break;
      case 'group_invitation':
        // TODO: Implement group invitation decline
        break;
      default:
        break;
    }

    if (onActionComplete) {
      onActionComplete('decline', notification);
    }
  };

  // Handle mark as read toggle
  const handleToggleRead = async (e) => {
    e.stopPropagation();
    
    if (!is_read) {
      await markAsRead(id);
    }
    // Note: Mark as unread functionality would need additional backend support
  };

  return (
    <div 
      className={`NTS-notification-item ${compact ? 'compact' : ''} ${is_read ? 'read' : 'unread'} ${priorityClass}`}
      onClick={handleNotificationClick}
    >
      {/* Notification content */}
      <div className="NTS-notification-content">
        {/* Actor avatar and icon */}
        <div className="NTS-notification-avatar">
          {actor_avatar_url ? (
            <img 
              src={actor_avatar_url} 
              alt={actor_name} 
              className="NTS-actor-avatar"
            />
          ) : (
            <div className="NTS-default-avatar">
              {actor_name ? actor_name.charAt(0).toUpperCase() : '?'}
            </div>
          )}
          
          <div 
            className="NTS-notification-type-icon"
            style={{ backgroundColor: categoryColor }}
          >
            <IconComponent />
          </div>
        </div>

        {/* Message and metadata - USING TRANSLATED MESSAGE */}
        <div className="NTS-notification-body">
          <div className="NTS-notification-message">
            {compact ? truncateMessage(displayMessage, 50) : displayMessage}
          </div>
          
          <div className="NTS-notification-meta">
            <span className="NTS-actor-name">{actor_name}</span>
            <span className="NTS-notification-time">
              {formatRelativeTime(created_at)}
            </span>
            {priority === 'high' || priority === 'urgent' ? (
              <span className={`NTS-priority-indicator ${priority}`}>
                {priority === 'urgent' ? '🔴' : '🟡'}
              </span>
            ) : null}
          </div>
        </div>

        {/* Read status indicator */}
        <div className="NTS-notification-status">
          {!is_read && <div className="NTS-unread-dot"></div>}
        </div>
      </div>

      {/* Action buttons */}
      {showActions && (
        <div className="NTS-notification-actions">
          {/* Only show accept/decline for specific invitation types and only when compact=false */}
          {!compact && needsAction && !is_read && (
            type === 'event_invitation' || 
            type === 'group_invitation'
          ) && (
            <div className="NTS-action-buttons">
              <CustomButton10Ca
                label={actionLabels.accept}
                onClick={handleAccept}
              >
                <FaCheck />
              </CustomButton10Ca>
              
              <CustomButton10Co
                label={actionLabels.decline}
                onClick={handleDecline}
              >
                <FaTimes />
              </CustomButton10Co>
            </div>
          )}

          {/* Read/Unread toggle - only show in non-compact mode */}
          {!compact && (
            <button
              className="NTS-read-toggle"
              onClick={handleToggleRead}
              title={is_read ? t('notifications.mark_unread') : t('notifications.mark_read')}
              aria-label={is_read ? (t('notifications.mark_unread') || 'Mark as unread') : (t('notifications.mark_read') || 'Mark as read')}
            >
              {is_read ? <FaEyeSlash /> : <FaEye />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
